import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, FilePlus, Paperclip, AlertTriangle, CheckCircle2, Download, Upload } from "lucide-react";

import { getBackendBase } from "@/lib/api";
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

/**
 * Public agent COI upload page (no auth).
 * Lives at /upload-coi/:token — accessible via the link emailed to the agent.
 */
export default function CoiUploadPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [file, setFile] = useState(null);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/coi-upload/${token}`);
                setInfo(data);
            } catch (err) {
                setErrorCode(err?.response?.status);
                setError(err?.response?.data?.detail || err?.message || "This link could not be loaded.");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file) { setError("Please choose a certificate file to upload."); return; }
        if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
            setError("File must be PDF, JPG, or PNG."); return;
        }
        setBusy(true); setError(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (notes.trim()) fd.append("agent_notes", notes.trim());
            await axios.post(`${BACKEND_URL}/api/coi-upload/${token}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDone(true);
        } catch (err) {
            setErrorCode(err?.response?.status);
            setError(err?.response?.data?.detail || err?.message || "Upload failed. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <PublicShell><p className="text-sm text-slate-500 text-center py-20">Loading…</p></PublicShell>;
    }

    if (done) {
        return (
            <PublicShell>
                <Card className="shadow-sm border-emerald-200" data-testid="coi-upload-success">
                    <CardContent className="p-10 text-center">
                        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700 ring-4 ring-emerald-100">
                            <CheckCircle2 size={28} />
                        </div>
                        <h1 className="mt-6 font-display text-2xl font-bold text-slate-900">Thanks — certificate received.</h1>
                        <p className="mt-3 text-sm text-slate-600 max-w-lg mx-auto">
                            RentFlow&apos;s team will review and notify the customer. You can safely close this tab.
                        </p>
                        {info?.customer?.company_name && (
                            <p className="mt-6 text-xs text-slate-500">
                                Filed under: <strong>{info.customer.company_name}</strong>
                                {info.equipment?.unit_number ? ` · unit ${info.equipment.unit_number}` : ""}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </PublicShell>
        );
    }

    if (error && !info) {
        const is410 = errorCode === 410;
        return (
            <PublicShell>
                <Card className={is410 ? "border-amber-200" : "border-red-200"} data-testid="coi-upload-error">
                    <CardContent className="p-10 text-center">
                        <div className={`mx-auto h-12 w-12 rounded-full flex items-center justify-center ring-4 ${
                            is410 ? "bg-amber-50 text-amber-700 ring-amber-100" : "bg-red-50 text-red-700 ring-red-100"
                        }`}>
                            <AlertTriangle size={22} />
                        </div>
                        <h1 className="mt-5 font-display text-xl font-semibold text-slate-900">
                            {is410 ? "This upload link is no longer active." : "We couldn't load this link."}
                        </h1>
                        <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto" data-testid="coi-upload-error-message">{error}</p>
                        <p className="mt-5 text-xs text-slate-500">
                            Please ask the requester to resend a fresh link, or reply to the original email with the certificate attached.
                        </p>
                    </CardContent>
                </Card>
            </PublicShell>
        );
    }

    const customer = info.customer || {};
    const eq = info.equipment || {};
    const req = info.requirements || {};
    const holder = info.certificate_holder || {};
    const expires = info.upload_token_expires_at ? new Date(info.upload_token_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

    return (
        <PublicShell>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6" data-testid="coi-upload-page">
                <div className="space-y-6">
                    <Card>
                        <CardContent className="p-6 space-y-5">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                                    <ShieldCheck size={18} />
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-emerald-800">Certificate of Insurance — Upload</p>
                                    <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-0.5">
                                        {customer.company_name || "Equipment rental"}
                                    </h1>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {customer.dba ? `DBA ${customer.dba} · ` : ""}USDOT {customer.usdot_number || "—"}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={onSubmit} className="space-y-4" data-testid="coi-upload-form">
                                <div className="space-y-1.5">
                                    <Label className="text-sm font-medium text-slate-900">Certificate file</Label>
                                    <label htmlFor="coi-upload-file"
                                        className={`flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed cursor-pointer transition-colors p-8 ${
                                            file ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"
                                        }`}
                                        data-testid="coi-upload-dropzone">
                                        {file ? (
                                            <>
                                                <Paperclip size={20} className="text-emerald-700" />
                                                <p className="text-sm font-medium text-emerald-900">{file.name}</p>
                                                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB · click to replace</p>
                                            </>
                                        ) : (
                                            <>
                                                <FilePlus size={22} className="text-slate-400" />
                                                <p className="text-sm text-slate-700 font-medium">Click to choose a certificate file</p>
                                                <p className="text-xs text-slate-500">PDF, JPG, or PNG · up to 10 MB</p>
                                            </>
                                        )}
                                        <input id="coi-upload-file" type="file" accept="application/pdf,image/jpeg,image/png"
                                            data-testid="coi-upload-file-input"
                                            className="hidden"
                                            onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                    </label>
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="coi-upload-notes" className="text-sm font-medium text-slate-900">Notes (optional)</Label>
                                    <Textarea id="coi-upload-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                                        placeholder="Anything we should know about this certificate (carrier, policy period, special endorsements)."
                                        data-testid="coi-upload-notes" />
                                    <p className="text-[11px] text-slate-400 text-right">{notes.length} / 500</p>
                                </div>

                                {error && (
                                    <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700 flex items-start gap-2" data-testid="coi-upload-inline-error">
                                        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
                                    <p className="text-xs text-slate-500">Link expires {expires}.</p>
                                    <Button type="submit" disabled={busy || !file} className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="coi-upload-submit">
                                        <Upload size={14} /> {busy ? "Uploading…" : "Upload certificate"}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card>
                        <CardContent className="p-5 space-y-3" data-testid="coi-upload-request-summary">
                            <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Equipment</p>
                            <dl className="text-sm space-y-1.5">
                                <Term label="Type" value={eq.type_label || "—"} />
                                {eq.unit_number && <Term label="Unit" value={eq.unit_number} />}
                                {(eq.year || eq.make || eq.model) && (
                                    <Term label="Make/Model" value={[eq.year, eq.make, eq.model].filter(Boolean).join(" ")} />
                                )}
                                {eq.vin && <Term label="VIN" value={eq.vin} />}
                                {eq.plate && <Term label="Plate" value={eq.plate} />}
                                <Term label="Period" value={`${eq.start_date || "—"} → ${eq.end_date || "open"}`} />
                            </dl>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-5 space-y-3">
                            <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Certificate holder</p>
                            <p className="text-sm font-medium text-slate-900">{holder.name || "—"}</p>
                            {holder.address && <p className="text-xs text-slate-600 whitespace-pre-line">{holder.address}</p>}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-5 space-y-3" data-testid="coi-upload-requirements">
                            <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500">Required coverage</p>
                            <dl className="text-sm space-y-1">
                                <Term label="GL" value={req.general_liability_limit || "Not specified"} />
                                <Term label="Auto" value={req.auto_liability_limit || "Not specified"} />
                                <Term label="Physical damage" value={req.physical_damage_limit || "Not specified"} />
                                <Term label="Waiver of subrogation" value={req.waiver_of_subrogation ? "Yes" : "No"} />
                                <Term label="Notice of cancellation" value={`${req.notice_of_cancellation_days || 30} days`} />
                            </dl>
                            {(req.additional_insured || []).length > 0 && (
                                <div className="pt-2 border-t border-slate-100">
                                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Additional insureds</p>
                                    <ul className="text-xs text-slate-700 space-y-0.5">
                                        {req.additional_insured.map((a, i) => <li key={i}>• {a}</li>)}
                                    </ul>
                                </div>
                            )}
                            {req.other_requirements && (
                                <div className="pt-2 border-t border-slate-100">
                                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Other</p>
                                    <p className="text-xs text-slate-700 whitespace-pre-line">{req.other_requirements}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {info.has_request_pdf && (
                        <a href={`${BACKEND_URL}${info.request_pdf_url}`} target="_blank" rel="noreferrer"
                            data-testid="coi-upload-request-pdf-link"
                            className="block text-center text-sm text-blue-900 hover:underline py-2 border border-slate-200 rounded-md hover:bg-slate-50">
                            <Download size={14} className="inline -mt-0.5 mr-1.5" /> Download original request PDF
                        </a>
                    )}
                </div>
            </div>
        </PublicShell>
    );
}

function PublicShell({ children }) {
    return (
        <div className="min-h-screen bg-slate-50">
            <header className="border-b border-slate-200 bg-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-2.5">
                    <img src="/favicon.png" alt="RentFlow" className="h-7 w-7 rounded-md" />
                    <div className="leading-tight">
                        <div className="font-display font-bold text-slate-900 text-base">RentFlow</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Agent portal</div>
                    </div>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {children}
            </main>
        </div>
    );
}

function Term({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="text-sm text-slate-900 text-right truncate">{value}</dd>
        </div>
    );
}
